'use client';

import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trackingManager } from '@/lib/tracking/tracking-manager-instance';

interface EmailSubscribeFormProps {
  source?: string;
  placeholder?: string;
  buttonText?: string;
  successMessage?: string;
  className?: string;
  locale?: 'de' | 'en' | 'pt';
}

export function EmailSubscribeForm({
  source,
  placeholder = 'your@email.com',
  buttonText = 'Subscribe',
  successMessage = 'Check your inbox!',
  className = '',
  locale,
}: EmailSubscribeFormProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation
    if (!validateEmail(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setStatus('idle');
    setMessage('');

    try {
      const response = await fetch('/api/email-leads/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          source,
          locale,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setMessage(successMessage);
        setEmail('');

        // Track successful subscription
        trackingManager.trackEvent('LANDING_NEWSLETTER_SUBSCRIBED', {
          source: source || 'unknown',
          language: locale || 'unknown',
        });
      } else {
        setStatus('error');
        // Handle different error messages
        if (data.error === 'ALREADY_SUBSCRIBED') {
          setMessage('This email is already subscribed');
        } else if (data.error === 'UNSUBSCRIBED') {
          setMessage('This email has been unsubscribed');
        } else {
          setMessage(data.message || 'Failed to subscribe. Please try again.');
        }
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error. Please try again.');
      console.error('Subscribe error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={className}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-2 max-w-md mx-auto sm:flex-row">
        <div className="flex-1">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={placeholder}
            disabled={isLoading}
            className="bg-background"
            aria-label="Email address"
            aria-invalid={status === 'error'}
            aria-describedby={status !== 'idle' ? 'subscribe-message' : undefined}
          />
        </div>
        <Button
          type="submit"
          disabled={isLoading}
          className="cursor-pointer bg-primary text-primary-foreground"
          aria-label={buttonText}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg
                className="h-5 w-5 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="hidden sm:inline">Subscribing...</span>
            </span>
          ) : (
            buttonText
          )}
        </Button>
      </form>

      {/* Status Messages */}
      {status !== 'idle' && (
        <p
          id="subscribe-message"
          className={`mt-2 text-sm text-center ${
            status === 'success'
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          }`}
          role={status === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          {message}
        </p>
      )}
    </div>
  );
}
